import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { ApiService } from "../../services/api";
import { CreateMeetingData } from "../../types/meeting";
import checkPermission from "../../utils/permission";
import ParticipantManager from "./components/ParticipantManager";
import { useModal } from "../../hooks/useModal";

export default function EditMeeting() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useModal();

  useEffect(() => {
    if (!checkPermission("meet_update")) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const pad = (n: number) => String(n).padStart(2, "0");
  const getCurrentDateTime = () => {
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const [formData, setFormData] = useState<CreateMeetingData>({
    title: "",
    description: "",
    startDate: getCurrentDateTime(),
    endDate: getCurrentDateTime(),
    referenceId: "",
    referenceType: "",
    metadata: {},
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700";

  useEffect(() => {
    const fetchMeeting = async () => {
      if (!id) {
        navigate("/meetings", { replace: true });
        return;
      }
      try {
        const res = await ApiService.getMeeting(id);
        const data = (res as any).data || res;
        const meeting = data?.data || data;
        const toLocal = (dt: string) => {
          if (!dt) return getCurrentDateTime();
          const d = new Date(dt);
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        };

        setFormData({
          title: meeting.title || "",
          description: meeting.description || "",
          startDate: toLocal(meeting.startDate),
          endDate: toLocal(meeting.endDate),
          referenceId: meeting.referenceId || "",
          referenceType: meeting.referenceType || "",
          metadata: {},
        });
      } catch (err: any) {
        showAlert({
          title: "Toplantı Bulunamadı",
          message: "Aradığınız toplantı bulunamadı veya silinmiş olabilir. Toplantılar sayfasına yönlendiriliyorsunuz.",
          type: "error",
        }).then(() => {
          navigate("/meetings", { replace: true });
        });
      } finally {
        setInitialLoading(false);
      }
    };
    fetchMeeting();
  }, [id, navigate, showAlert]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "startDate" || name === "endDate") {
      let corrected = value;
      if (value) {
        const [datePart] = value.split("T");
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        if (datePart === todayStr) {
          const chosen = new Date(value);
          const floorNow = new Date(`${todayStr}T${pad(now.getHours())}:${pad(now.getMinutes())}`);
          if (chosen.getTime() < floorNow.getTime()) {
            corrected = `${todayStr}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
          }
        }
      }
      setFormData((prev) => ({ ...prev, [name]: corrected }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = "Toplantı başlığı gereklidir";
    if (!formData.description.trim()) newErrors.description = "Toplantı açıklaması gereklidir";
    if (!formData.startDate) newErrors.startDate = "Başlangıç tarihi gereklidir";
    if (!formData.endDate) newErrors.endDate = "Bitiş tarihi gereklidir";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload: any = {
        title: formData.title.trim(),
        description: formData.description.trim(),

        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        metadata: {},
      };

      await ApiService.updateMeeting(id, payload);
      localStorage.setItem("meetingListShouldRefresh", "true");
      navigate("/meetings", { replace: true });
    } catch (err) {
      await showAlert({
        title: "Hata",
        message: "Toplantı güncellenemedi.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="h-[calc(100vh-72px)] bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const isMeetingStarted = new Date(formData.startDate).getTime() < new Date().getTime();

  return (
    <>
      <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-hidden flex flex-col">
          <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
            <div className="px-6 py-3">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">Toplantılar</span>
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <button
                    onClick={() => navigate("/meetings")}
                    className="pb-3 transition-all duration-200 text-blue-600 font-medium border-b-2 border-blue-500">
                    Tüm Toplantılar
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Toplantıyı Düzenle</h1>
                    <p className="text-sm text-gray-600 mt-1">Toplantı bilgilerini güncelleyin ve katılımcıları yönetin.</p>
                    <div className="mt-3 h-1 w-20 rounded-full bg-blue-200" />
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/meetings")}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                    Geri Dön
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Temel Bilgiler</span>
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Toplantı Başlığı *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`${fieldClassName} ${
                        errors.title ? "border-red-300 bg-red-50" : ""
                      }`}
                      placeholder="Toplantı başlığını buraya yazın..."
                      required
                    />
                    {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                    </div>
                  </div>

                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Açıklama</span>
                    </div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className={`${fieldClassName} ${
                        errors.description ? "border-red-300 bg-red-50" : ""
                      }`}
                      placeholder="Toplantının amacını ve gündemini burada açıklayın..."
                    />
                    {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                  </div>

                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Tarih ve Saat</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi ve Saati *</label>
                      <input
                        type="datetime-local"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        min={getCurrentDateTime()}
                        disabled={isMeetingStarted}
                        className={`${fieldClassName} ${
                          errors.startDate ? "border-red-300 bg-red-50" : ""
                        } ${isMeetingStarted ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                      />
                      {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
                      </div>

                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi ve Saati *</label>
                      <input
                        type="datetime-local"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        min={getCurrentDateTime()}
                        disabled={isMeetingStarted}
                        className={`${fieldClassName} ${
                          errors.endDate ? "border-red-300 bg-red-50" : ""
                        } ${isMeetingStarted ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                      />
                      {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
                      </div>
                    </div>
                  </div>

                  <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => navigate("/meetings")}
                        className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                        İptal
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-sm">
                        {loading ? "Güncelleniyor..." : "Güncelle"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Katılımcı Yönetimi</h3>
                </div>
                {id ? (
                  <ParticipantManager meetingId={id} />
                ) : (
                  <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-red-50 rounded-xl border-2 border-dashed border-gray-300">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Toplantı yüklenemedi</h3>
                    <p className="text-gray-600">Katılımcı bilgileri yüklenirken bir hata oluştu</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
